import React, { useState, useEffect } from "react";
import { DateTime } from "luxon";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight, mdiLoading, mdiAirplaneTakeoff, mdiCalendarClock, mdiEmailOutline } from "@mdi/js";
import { availabilityApi } from "../api/availability";
import "./PublicBookingCalendar.css";

/**
 * Public-facing booking calendar for instructor profiles.
 * Displays real availability from backend merge-view API.
 * Shows time periods (Morning, Midday, Afternoon, Evening) with slot availability.
 *
 * @param {string} teacherId - Instructor organization ID
 * @param {function} onSlotClick - Callback when user clicks a day with availability
 * @param {string} variant - Display variant: "phone" (compact inside phone mockup) or "desktop" (full-size)
 * @param {boolean} disabled - When true, the calendar renders read-only: availability is still
 *   shown but days are not clickable (used when the instructor is not accepting public bookings)
 */
const PublicBookingCalendar = ({ teacherId, onSlotClick, variant = "desktop", disabled = false, onEnquire }) => {
    const [currentMonth, setCurrentMonth] = useState(DateTime.now());
    const [calendarData, setCalendarData] = useState({});
    const [travelPeriods, setTravelPeriods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Time periods — 3 periods, Afternoon covers afternoon + evening
    const TIME_PERIODS = [
        { name: "Morning", start: 7, end: 11 },
        { name: "Midday", start: 11, end: 14 },
        { name: "Afternoon", start: 14, end: 22 },
    ];

    // Load availability data when month changes
    useEffect(() => {
        loadAvailability();
    }, [currentMonth, teacherId]);

    const loadAvailability = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDate = currentMonth.startOf("month").toISODate();
            const endDate = currentMonth.endOf("month").toISODate();

            // Fetch merged calendar (recurring + overrides + contexts)
            const calendar = await availabilityApi.getMergedCalendar(teacherId, startDate, endDate);
            setCalendarData(calendar);

            // Fetch travel periods for display
            const travels = await availabilityApi.getTravelPeriods(teacherId, {
                startDate,
                endDate,
            });
            setTravelPeriods(travels);
        } catch (err) {
            console.error("Error loading availability:", err);
            setError("Failed to load availability. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Navigate to previous month
    const goToPreviousMonth = () => {
        const newMonth = currentMonth.minus({ months: 1 });
        // Block navigation to past months
        const today = DateTime.now().startOf("month");
        if (newMonth >= today) {
            setCurrentMonth(newMonth);
        }
    };

    // Navigate to next month
    const goToNextMonth = () => {
        setCurrentMonth(currentMonth.plus({ months: 1 }));
    };

    // Check if date is in travel period.
    // Compare YYYY-MM-DD strings (timezone-agnostic) so that a travel
    // period stored as 2026-06-14T00:00:00Z still matches the calendar
    // cell for June 14 regardless of the viewer's local timezone offset.
    const getTravelPeriodForDate = date => {
        const dateKey = date.toISODate();
        return travelPeriods.find(travel => {
            const startKey = (travel.startDate || "").split("T")[0];
            const endKey = (travel.endDate || "").split("T")[0];
            return dateKey >= startKey && dateKey <= endKey;
        });
    };

    // Calculate availability state for a time period
    const getPeriodState = (slots, period) => {
        if (!slots || slots.length === 0) return "none";

        const periodSlots = slots.filter(slot => {
            const [hours] = slot.startTime.split(":").map(Number);
            return hours >= period.start && hours < period.end;
        });

        if (periodSlots.length === 0) return "none";

        const availableSlots = periodSlots.filter(s => s.status === "available");
        const bookedSlots = periodSlots.filter(s => s.status === "booked");

        if (availableSlots.length === 0) return "booked"; // Fully booked
        if (bookedSlots.length === 0) return "available"; // Fully available
        return "partial"; // Partially available
    };

    // Generate calendar days
    const generateCalendarDays = () => {
        const days = [];
        const firstDay = currentMonth.startOf("month");
        const lastDay = currentMonth.endOf("month");
        const today = DateTime.now().startOf("day");

        // Add empty cells for days before month starts
        const startPadding = firstDay.weekday === 7 ? 0 : firstDay.weekday; // Sunday = 0
        for (let i = 0; i < startPadding; i++) {
            days.push({ date: null, isEmpty: true });
        }

        // Add days of month
        for (let day = 1; day <= lastDay.day; day++) {
            const date = currentMonth.set({ day });
            const dateKey = date.toISODate();
            const slots = calendarData[dateKey] || [];
            const travel = getTravelPeriodForDate(date);
            const isPast = date.startOf("day") < today;

            days.push({
                date,
                dateKey,
                isToday: date.hasSame(today, "day"),
                isPast,
                isTravel: !!travel,
                travelDestination: travel?.destination,
                travelColor: travel?.color,
                travelPeriod: travel, // Include full travel period object for TimeSlotPickerModal
                slots,
                periods: TIME_PERIODS.map(period => ({
                    ...period,
                    state: getPeriodState(slots, period),
                })),
            });
        }

        return days;
    };

    // Handle slot click
    const handleDayClick = day => {
        if (disabled) return; // instructor not accepting public bookings — read-only calendar
        if (day.isEmpty || day.isPast || !day.slots || day.slots.length === 0) return;
        if (onSlotClick) {
            onSlotClick(day);
        }
    };

    const calendarDays = generateCalendarDays();

    const isPhoneVariant = variant === "phone";

    return (
        <div className={`public-booking-calendar ${isPhoneVariant ? "phone-calendar" : ""}`}>
            {/* Month navigation */}
            <div className="calendar-nav">
                <button
                    onClick={goToPreviousMonth}
                    className="calendar-nav-btn"
                    aria-label="Previous month"
                    disabled={currentMonth.startOf("month") <= DateTime.now().startOf("month")}>
                    <Icon path={mdiChevronLeft} size={1} />
                </button>
                <h3 className="calendar-month-title">{currentMonth.toFormat("MMMM yyyy")}</h3>
                <button onClick={goToNextMonth} className="calendar-nav-btn" aria-label="Next month">
                    <Icon path={mdiChevronRight} size={1} />
                </button>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="calendar-loading">
                    <Icon path={mdiLoading} size={1.5} spin />
                    <p>Loading availability...</p>
                </div>
            )}

            {/* Error state */}
            {error && (
                <div className="calendar-error">
                    <p>{error}</p>
                    <button onClick={loadAvailability} className="calendar-retry-btn">
                        Retry
                    </button>
                </div>
            )}

            {/* Calendar + vertical legend side by side */}
            {!loading && !error && (
                <div className="calendar-with-legend">
                    {/* Vertical legend */}
                    <div className="calendar-legend-vertical">
                        <span className="legend-section-label">Key</span>
                        <div className="legend-vertical-item">
                            <div className="schedule-legend-badge badge--available"></div>
                            <span>Available</span>
                        </div>
                        <div className="legend-vertical-item">
                            <div className="schedule-legend-badge badge--partial"></div>
                            <span>Partial bookings</span>
                        </div>
                        <div className="legend-vertical-item">
                            <div className="schedule-legend-badge badge--booked"></div>
                            <span>Fully booked</span>
                        </div>
                        {travelPeriods.length > 0 && (
                            <>
                                <span className="legend-section-label" style={{ marginTop: "12px" }}>
                                    Travel
                                </span>
                                {travelPeriods.map((travel, idx) => {
                                    const color = travel.color;
                                    return (
                                        <div
                                            key={idx}
                                            className="legend-travel-pill"
                                            style={{ backgroundColor: `${color}20`, border: `1.5px solid ${color}` }}>
                                            <Icon path={mdiAirplaneTakeoff} size={0.7} color={color} />
                                            <span>{travel.location?.description || travel.destination}</span>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>

                    <div className="calendar-main">
                        {/* Day names */}
                        <div className="calendar-weekdays">
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                                <div key={day} className="calendar-weekday">
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Calendar days. When the instructor is not accepting public
                            bookings the grid is covered by a scrim so it's visibly locked
                            (availability still readable beneath) while month nav + travel
                            key stay usable. */}
                        <div className={`calendar-grid-wrap ${disabled ? "locked" : ""}`}>
                        <div className="calendar-grid">
                            {calendarDays.map((day, idx) => (
                                <div
                                    key={idx}
                                    className={`calendar-day ${day.isEmpty ? "empty" : ""} ${day.isToday ? "today" : ""} ${
                                        day.isPast ? "past-date" : ""
                                    } ${day.isTravel ? "travel" : ""} ${day.slots?.length > 0 && !day.isPast && !disabled ? "clickable" : "no-slots"}`}
                                    onClick={() => handleDayClick(day)}
                                    title={day.isTravel ? `Teaching at ${day.travelDestination}` : ""}
                                    style={
                                        day.isTravel
                                            ? {
                                                  border: `2px solid ${day.travelColor}`,
                                                  boxShadow: `0 0 0 1px ${day.travelColor}40`,
                                                  backgroundColor: `${day.travelColor}20`,
                                              }
                                            : {}
                                    }>
                                    {!day.isEmpty && (
                                        <>
                                            <div className="calendar-day-number">{day.date.day}</div>
                                            <div className="calendar-periods">
                                                {day.periods.map((period, i) => (
                                                    <div key={i} className={`period-badge ${period.state}`}>
                                                        {period.state !== "none" ? period.name : ""}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        {disabled && (
                            <div className="calendar-grid-scrim">
                                <button
                                    type="button"
                                    className="calendar-grid-scrim-card"
                                    onClick={onEnquire}
                                >
                                    <Icon path={mdiCalendarClock} size={1} className="scrim-lock-icon" />
                                    <span>Online booking isn't open with this instructor at the moment. Send them a message to enquire about lessons and availability, and they'll be in touch.</span>
                                    <span className="scrim-cta">
                                        <Icon path={mdiEmailOutline} size={0.8} />
                                        Send a Message
                                    </span>
                                </button>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PublicBookingCalendar;
